import { BudgetLineBox, BudgetLineSelect, commitToLine, useBudgetFor } from '../../lib/budget.tsx';
import { BankDetailsFields, bankDetailsErrors, bankDetailsOf, bankDetailsValues, useUsBanking } from '../../components/BankDetails.tsx';
import { useTaxRates } from '../../lib/taxes.ts';
import { idError, idHint } from '../../lib/country.ts';
import { CustomFieldInputs, CustomFieldsView, useCustomFields } from '../../components/CustomFields.tsx';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../../components/ui.tsx';
import { t, tn, type MessageKey } from '../../i18n/index.ts';
import { action, records } from '../../lib/api.ts';
import { baseCurrency, dateTime, day, money, moneyIn, parseMoney, rateText, todayIso } from '../../lib/format.ts';
import { CurrencyFields, effectiveRate, toBase, useCurrencies, useDayRate } from './Currencies.tsx';
import { usePlaces } from '../../lib/places.tsx';
import { useSession } from '../../lib/session.tsx';
import { useUi } from '../../lib/ui-state.tsx';
import { ManageLayout, useAction, useSave } from './common.tsx';
import { LandedDialog, LandedList } from './LandedCosts.tsx';
import { emptyLots, LotsField, readLots, type LotDraft, type Tracking } from './Lots.tsx';
import { costText, parseCost, parseQty, unitLabel, withUnit, type Overview, type Unit } from './Stock.tsx';
import { PackSelect } from '../../components/Packs.tsx';
import { AgedTable, type AgedData } from '../FollowUp.tsx';
import { RfqTab, SupplierPrices } from './Rfq.tsx';
import { useLinkParam } from '../../lib/link-params.ts';
import { RecordNotes } from '../../components/RecordNotes.tsx';

type OrderState = 'draft' | 'ordered' | 'partial' | 'received' | 'cancelled';
type BillState = 'open' | 'paid' | 'cancelled';
type PayMethod = 'bank' | 'cash' | 'till' | 'card';
interface Supplier {
  id: string;
  name: string;
  tax_id: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  payment_days: number;
  iban?: string | null;
  bic?: string | null;
  routing_no?: string | null;
  account_no?: string | null;
  account_type?: 'checking' | 'savings' | null;
  note: string | null;
  active: boolean;
  open_balance?: number;
  overdue?: number;
  open_orders: number;
  last_order: string | null;
}
interface OrderRow {
  id: string;
  number: number;
  supplier: string;
  supplier_name: string;
  state: OrderState;
  expected: string | null;
  note: string | null;
  total: number;
  lines: number;
  received_value: number;
  created_at: string;
  location?: string;
  location_name?: string;
  /** Dropship: the supplier delivers straight to a customer. */
  dropship?: boolean;
  ship_name?: string | null;
  ship_address?: string | null;
  ship_phone?: string | null;
  customer_ref?: string | null;
}
interface OrderLine {
  id: string;
  item: string;
  name: string;
  unit: Unit;
  qty: number;
  unit_cost: number;
  received: number;
  total: number;
  on_hand: number;
  tracking?: Tracking;
  /** Ordered in packs: the pack's name and how many units it held. */
  pack?: string | null;
  pack_qty?: number | null;
}
interface OrderDetail {
  order: OrderRow & { name: string; cancel_reason: string | null; ordered_at: string | null };
  supplier: Supplier;
  lines: OrderLine[];
  receipts: { id: string; name: string; unit: Unit; qty: number; value: number; at: string; by_name: string | null }[];
  bills: Bill[];
  deliveries?: { id: string; value: number; note: string | null; at: string; by_name: string | null }[];
}
interface Bill {
  id: string;
  number: number;
  supplier: string;
  supplier_name?: string;
  supplier_ref: string | null;
  order: string | null;
  order_number?: number | null;
  date: string;
  due_date: string;
  total: number;
  tax: number;
  paid: number;
  state: BillState;
  note: string | null;
  days_overdue?: number;
  /** A bill in another currency: its code, the rate it was booked at and its own figures. */
  currency?: string | null;
  rate?: number | null;
  fx_total?: number | null;
  fx_tax?: number | null;
  fx_paid?: number | null;
}
interface BillDetail {
  bill: Bill & { name: string; cancel_reason: string | null };
  supplier: Supplier;
  order: { id: string; name: string } | null;
  payments: {
    id: string;
    amount: number;
    method: PayMethod;
    reference: string | null;
    paid_on: string;
    at: string;
    by_name: string | null;
    fx_amount?: number | null;
    rate?: number | null;
    fx_diff?: number | null;
  }[];
}
interface PurchaseOverview {
  today: string;
  orders: { drafts: number; to_receive: number; late: number };
  payables?: { open: number; overdue: number; overdue_bills: number; due_week: number };
}

const po = (n: number) => `PO-${n}`;
const billNo = (n: number) => `BILL-${n}`;

const ORDER_BADGE: Record<OrderState, { tone: Tone; icon: IconName; label: MessageKey }> = {
  draft: { tone: 'neutral', icon: 'edit', label: 'buy.state_draft' },
  ordered: { tone: 'info', icon: 'send', label: 'buy.state_ordered' },
  partial: { tone: 'warn', icon: 'clock', label: 'buy.state_partial' },
  received: { tone: 'ok', icon: 'check', label: 'buy.state_received' },
  cancelled: { tone: 'neutral', icon: 'x', label: 'buy.state_cancelled' },
};
function OrderBadge({ o, today }: { o: { state: OrderState; expected: string | null }; today: string }) {
  if ((o.state === 'ordered' || o.state === 'partial') && o.expected && o.expected < today) {
    return <StatusBadge tone="danger" icon="alert" label={t('buy.late')} />;
  }
  const b = ORDER_BADGE[o.state];
  return <StatusBadge tone={b.tone} icon={b.icon} label={t(b.label)} />;
}
function BillBadge({ b, today }: { b: Bill; today: string }) {
  if (b.state === 'paid') return <StatusBadge tone="ok" icon="check" label={t('buy.bill_paid')} />;
  if (b.state === 'cancelled') return <StatusBadge tone="neutral" icon="x" label={t('buy.state_cancelled')} />;
  if (b.due_date < today) return <StatusBadge tone="danger" icon="alert" label={t('buy.overdue')} />;
  if (b.paid > 0) return <StatusBadge tone="warn" icon="clock" label={t('buy.bill_part')} />;
  return <StatusBadge tone="info" icon="clock" label={t('buy.bill_open')} />;
}

type Tab = 'orders' | 'rfqs' | 'bills' | 'suppliers';

/** Buying in one place: orders to suppliers, what they deliver, their bills and paying them. */
export function Purchasing() {
  const { can } = useSession();
  const manage = can('purchase.manage');
  const [params, setParams] = useSearchParams();
  const tab = (['orders', 'rfqs', 'bills', 'suppliers'].includes(params.get('tab') ?? '') ? params.get('tab') : 'orders') as Tab;
  const { data: overview, reload: reloadOverview } = useAction<PurchaseOverview>('purchase.overview');
  const [creating, setCreating] = useState<'order' | 'bill' | 'supplier' | 'rfq' | null>(null);
  // Quick create: /manage/purchasing?new=order | bill | supplier.
  useLinkParam('new', (kind) => manage && (kind === 'order' || kind === 'bill' || kind === 'supplier') && setCreating(kind));
  const [version, setVersion] = useState(0);
  const changed = async () => {
    setVersion((v) => v + 1);
    await reloadOverview();
  };
  const tabs: Tab[] = manage ? ['orders', 'rfqs', 'bills', 'suppliers'] : ['orders', 'rfqs', 'suppliers'];

  return (
    <ManageLayout
      title={t('manage.purchasing')}
      subtitle={t('buy.subtitle')}
      actions={
        manage ? (
          tab === 'bills' ? (
            <Button variant="primary" icon="plus" onClick={() => setCreating('bill')}>
              {t('buy.new_bill')}
            </Button>
          ) : tab === 'rfqs' ? (
            <Button variant="primary" icon="plus" onClick={() => setCreating('rfq')}>
              {t('rfq.new')}
            </Button>
          ) : tab === 'suppliers' ? (
            <Button variant="primary" icon="plus" onClick={() => setCreating('supplier')}>
              {t('buy.new_supplier')}
            </Button>
          ) : (
            <Button variant="primary" icon="plus" onClick={() => setCreating('order')}>
              {t('buy.new_order')}
            </Button>
          )
        ) : null
      }
    >
      {!overview ? (
        <Skeleton h={420} r="var(--radius-lg)" />
      ) : (
        <div className="stock">
          <dl className="fiscal-stats">
            <div className="fiscal-stat">
              <dt>{t('buy.k_drafts')}</dt>
              <dd>{overview.orders.drafts}</dd>
            </div>
            <div className={`fiscal-stat${overview.orders.late ? ' danger' : ''}`}>
              <dt>{t('buy.k_to_receive')}</dt>
              <dd>
                {overview.orders.to_receive}
                {overview.orders.late ? <small> · {tn('buy.k_late', overview.orders.late)}</small> : null}
              </dd>
            </div>
            {overview.payables ? (
              <>
                <div className="fiscal-stat">
                  <dt>{t('buy.k_owed')}</dt>
                  <dd>{money(overview.payables.open)}</dd>
                </div>
                <div className={`fiscal-stat${overview.payables.overdue ? ' danger' : ''}`}>
                  <dt>{t('buy.k_overdue')}</dt>
                  <dd>{money(overview.payables.overdue)}</dd>
                </div>
              </>
            ) : null}
          </dl>

          <div className="segmented tabs" role="tablist" aria-label={t('manage.purchasing')}>
            {tabs.map((k) => (
              <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => setParams(k === 'orders' ? {} : { tab: k }, { replace: true })}>
                {t(`buy.tab_${k}`)}
              </button>
            ))}
          </div>

          {tab === 'orders' ? <Orders key={version} today={overview.today} manage={manage} onChange={changed} /> : null}
          {tab === 'rfqs' ? <RfqTab manage={manage} onOrders={changed} creating={creating === 'rfq'} onCreated={() => setCreating(null)} /> : null}
          {tab === 'bills' && manage ? <Bills key={version} today={overview.today} onChange={changed} /> : null}
          {tab === 'bills' && manage ? <AgedPayables key={`aged${version}`} /> : null}
          {tab === 'suppliers' ? <Suppliers key={version} manage={manage} onChange={changed} /> : null}
        </div>
      )}
      {creating === 'order' ? <OrderEditor detail={null} title={t('buy.new_order')} onClose={() => setCreating(null)} onChange={changed} /> : null}
      {creating === 'bill' ? <NewBillDialog onClose={() => setCreating(null)} onChange={changed} /> : null}
      {creating === 'supplier' ? <SupplierDialog onClose={() => setCreating(null)} onChange={changed} /> : null}
    </ManageLayout>
  );
}

/* ---------------------------------------------------------------- Orders */

function Orders({ today, manage, onChange }: { today: string; manage: boolean; onChange: () => Promise<void> }) {
  const [filter, setFilter] = useState<'open' | 'to_receive' | 'done' | 'all'>('open');
  const { data, reload } = useAction<OrderRow[]>('purchase.orders', { filter });
  // A link from elsewhere (an approval, the search) opens one order: /manage/purchasing?open=<id>.
  const [open, setOpen] = useState<string | null>(null);
  useLinkParam('open', setOpen);
  const { multi } = usePlaces();
  return (
    <>
      <div className="toolbar">
        <div className="segmented" role="radiogroup" aria-label={t('stock.show')}>
          {(['open', 'to_receive', 'done', 'all'] as const).map((f) => (
            <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
              {t(`buy.of_${f}`)}
            </button>
          ))}
        </div>
      </div>
      <section className="list-card" aria-label={t('buy.tab_orders')}>
        {!data ? (
          <Skeleton h={160} />
        ) : data.length === 0 ? (
          <EmptyState icon="bag" title={t('buy.no_orders')} body={manage ? t('buy.no_orders_body') : undefined} />
        ) : (
          data.map((o) => (
            <button key={o.id} className="list-row stock-row" onClick={() => setOpen(o.id)}>
              <span className={`list-icon${o.dropship ? ' move-landed' : ''}`} aria-hidden="true">
                <Icon name={o.dropship ? 'truck' : 'bag'} size={18} />
              </span>
              <span className="list-main">
                <span className="list-title">
                  {po(o.number)} · {o.supplier_name}
                </span>
                <span className="list-sub">
                  {o.dropship ? (
                    <span className="place-tag ds-tag">{t('ds.tag', { name: o.ship_name ?? '' })}</span>
                  ) : multi && o.location_name ? (
                    <span className="place-tag">{o.location_name}</span>
                  ) : null}
                  {[
                    tn('buy.lines', o.lines),
                    o.expected ? t('buy.expected_on', { day: day(o.expected) }) : null,
                    o.state === 'partial' ? t('buy.received_value', { value: money(o.received_value) }) : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              <span className="stock-qty">
                <strong>{money(o.total)}</strong>
              </span>
              <OrderBadge o={o} today={today} />
            </button>
          ))
        )}
      </section>
      {open ? (
        <OrderDialog
          id={open}
          today={today}
          onClose={() => setOpen(null)}
          onChange={async () => {
            await Promise.all([reload(), onChange()]);
          }}
        />
      ) : null}
    </>
  );
}

interface EditLine {
  item: string;
  qty: string;
  cost: string;
  /** What the quantity and price are in: '' the item's own unit, or one of its packs. */
  pack: string;
  /** The price was filled in, not typed: it follows the supplier's price list. */
  auto?: boolean;
  /** Where the filled-in price came from. */
  listed?: boolean;
}

/** An existing order: edit while a draft, receive while open, read once done. */
function OrderDialog({ id, today, onClose, onChange }: { id: string; today: string; onClose: () => void; onChange: () => Promise<void> }) {
  const { can } = useSession();
  const { data: d, reload } = useAction<OrderDetail>('purchase.order', { order: id });
  const refresh = async () => {
    await Promise.all([reload(), onChange()]);
  };
  if (!d) {
    return (
      <Dialog title={t('buy.order')} onClose={onClose} size="lg">
        <Skeleton h={240} />
      </Dialog>
    );
  }
  const title = `${d.order.name} · ${d.supplier.name}`;
  if (d.order.state === 'draft' && can('purchase.manage')) return <OrderEditor detail={d} title={title} onClose={onClose} onChange={refresh} />;
  return <OrderView detail={d} today={today} title={title} onClose={onClose} onChange={refresh} />;
}

function OrderEditor({ detail, title, onClose, onChange }: { detail: OrderDetail | null; title: string; onClose: () => void; onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { toast } = useUi();
  const { data: suppliers } = useAction<Supplier[]>('purchase.suppliers');
  const places = usePlaces();
  const [place, setPlace] = useState(detail?.order.location ?? places.here?.id ?? '');
  const at = places.multi ? place : '';
  const { data: stock } = useAction<Overview>('inventory.overview', at ? { location: at } : {});
  const [supplier, setSupplier] = useState(detail?.order.supplier ?? '');
  const [expected, setExpected] = useState(detail?.order.expected ?? '');
  const [note, setNote] = useState(detail?.order.note ?? '');
  const [dropship, setDropship] = useState(!!detail?.order.dropship);
  const [ship, setShip] = useState({
    name: detail?.order.ship_name ?? '',
    address: detail?.order.ship_address ?? '',
    phone: detail?.order.ship_phone ?? '',
    ref: detail?.order.customer_ref ?? '',
  });
  const [lines, setLines] = useState<EditLine[]>(
    detail?.lines.map((l) =>
      l.pack && l.pack_qty
        ? { item: l.item, qty: String(Math.round((l.qty / l.pack_qty) * 1000) / 1000), cost: costText(Math.round(l.unit_cost * l.pack_qty * 1000) / 1000), pack: l.pack }
        : { item: l.item, qty: String(l.qty), cost: costText(l.unit_cost), pack: '' },
    ) ?? [{ item: '', qty: '', cost: '', pack: '' }],
  );
  const [confirmCancel, setConfirmCancel] = useState(false);
  const budget = useBudgetFor('purchase.order', detail?.order.id);
  const [budgetLine, setBudgetLine] = useState<string | null>(null);
  const lineNow = budgetLine ?? budget.data?.commitment?.line ?? '';
  const items = (stock?.items ?? []).filter((i) => i.active);
  const byId = new Map((stock?.items ?? []).map((i) => [i.id, i]));
  const parsed = lines.map((l) => ({ item: byId.get(l.item), qty: parseQty(l.qty), cost: parseCost(l.cost) }));
  const filled = parsed.filter((_, i) => lines[i]!.item || lines[i]!.qty || lines[i]!.cost);
  const bad = filled.some((l) => !l.item || !l.qty || l.qty <= 0 || l.cost === null);
  const twice = new Set(lines.filter((l) => l.item).map((l) => l.item)).size !== lines.filter((l) => l.item).length;
  const total = filled.reduce((s, l) => s + (l.qty && l.cost !== null ? Math.round(l.qty * l.cost) : 0), 0);
  const shipMissing = dropship && (!ship.name.trim() || !ship.address.trim());
  const canSave = supplier && filled.length > 0 && !bad && !twice && !shipMissing;
  const sup = suppliers?.find((s) => s.id === supplier);

  const set = (i: number, k: keyof EditLine, v: string) =>
    setLines(
      lines.map((l, j) => {
        if (j !== i) return l;
        const next = { ...l, [k]: v };
        if (k === 'item') next.pack = '';
        if (k === 'item' && (!l.cost || l.auto) && byId.get(v)) Object.assign(next, { cost: costText(byId.get(v)!.unit_cost), auto: true, listed: false });
        if (k === 'cost') Object.assign(next, { auto: false, listed: false });
        // A pack costs what its units cost.
        const it = byId.get(l.item);
        if (k === 'pack' && it) next.cost = costText(Math.round((v ? (it.packs?.find((p) => p.name === v)?.qty ?? 1) : 1) * it.unit_cost * 1000) / 1000);
        return next;
      }),
    );
  // Prices filled in follow the supplier's price list for the quantity ordered.
  const priceKey = `${supplier}|${lines.map((l) => (l.auto ? `${l.item}:${l.qty}:${l.pack}` : '-')).join(',')}`;
  useEffect(() => {
    const ask = lines.map((l, i) => ({ l, i, qty: parseQty(l.qty) })).filter((x) => x.l.auto && x.l.item && byId.get(x.l.item));
    if (!supplier || !ask.length) return;
    let gone = false;
    const per = (l: EditLine) => (l.pack ? (byId.get(l.item)?.packs?.find((p) => p.name === l.pack)?.qty ?? 1) : 1);
    void action<{ item: string; price: number | null }[]>('purchase.prices_for', { supplier, lines: ask.map((x) => ({ item: x.l.item, qty: (x.qty ?? 0) * per(x.l) })) })
      .then((got) => {
        if (gone) return;
        setLines((now) =>
          now.map((l, i) => {
            const n = ask.findIndex((x) => x.i === i);
            if (n < 0 || !l.auto || l.item !== ask[n]!.l.item) return l;
            const unit = got[n]!.price ?? byId.get(l.item)!.unit_cost;
            const cost = costText(Math.round(unit * per(l) * 1000) / 1000);
            return cost === l.cost && l.listed === (got[n]!.price !== null) ? l : { ...l, cost, listed: got[n]!.price !== null };
          }),
        );
      })
      .catch(() => {});
    return () => {
      gone = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceKey, stock]);
  const suggest = () =>
    void run(async () => {
      const s = await action<{ item: string; qty: number; unit_cost: number }[]>('purchase.suggest', { supplier, ...(at ? { location: at } : {}) });
      if (!s.length) return toast(t('buy.suggest_none', { name: sup?.name ?? '' }), 'info');
      const keep = lines.filter((l) => l.item && !s.some((x) => x.item === l.item));
      setLines([...keep, ...s.map((x) => ({ item: x.item, qty: String(x.qty), cost: costText(x.unit_cost), pack: '', auto: true }))]);
    });
  const save = (send: boolean) =>
    void run(
      async () => {
        const o = await action<{ id: string }>('purchase.save_order', {
          order: detail?.order.id,
          supplier,
          expected: expected || null,
          note: note.trim() || null,
          ...(at ? { location: at } : {}),
          lines: filled.map((l) => {
            const pack = lines[parsed.indexOf(l)]!.pack;
            return { item: l.item!.id, qty: l.qty, unit_cost: l.cost, ...(pack ? { pack } : {}) };
          }),
          dropship,
          ...(dropship ? { ship_name: ship.name.trim(), ship_address: ship.address.trim(), ship_phone: ship.phone.trim() || null, customer_ref: ship.ref.trim() || null } : {}),
        });
        if (budget.data && budgetLine !== null && budgetLine !== (budget.data.commitment?.line ?? '')) await commitToLine('purchase.order', o.id, budgetLine);
        if (send) await action('purchase.confirm', { order: o.id });
        await onChange();
        onClose();
      },
      send ? t('buy.sent', { name: sup?.name ?? '' }) : t('buy.draft_saved'),
    );
  const cancel = () =>
    void run(
      async () => {
        await action('purchase.cancel_order', { order: detail!.order.id, reason: t('buy.cancel_draft_reason') });
        await onChange();
        onClose();
      },
      t('buy.cancelled', { name: detail!.order.name }),
    );

  return (
    <Dialog
      title={title}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="muted">{t('stock.receive_total', { total: money(total) })}</span>
          <span className="foot-actions">
            {detail ? (
              <Button variant="ghost" icon="x" onClick={() => setConfirmCancel(true)} disabled={busy}>
                {t('buy.cancel_order')}
              </Button>
            ) : null}
            <Button icon="edit" onClick={() => save(false)} busy={busy} disabled={!canSave}>
              {t('buy.save_draft')}
            </Button>
            <Button variant="primary" icon="send" onClick={() => save(true)} busy={busy} disabled={!canSave}>
              {t('buy.send')}
            </Button>
          </span>
        </>
      }
    >
      {!suppliers || !stock ? (
        <Skeleton h={240} />
      ) : (
        <div className="form-stack">
          {confirmCancel ? (
            <div className="callout callout-warn confirm-row">
              <Icon name="alert" size={16} />
              <span>{t('buy.cancel_confirm')}</span>
              <Button variant="ghost" size="sm" onClick={() => setConfirmCancel(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" size="sm" onClick={cancel} busy={busy}>
                {t('buy.cancel_order')}
              </Button>
            </div>
          ) : null}
          <div className="form-grid">
            <Field label={t('buy.supplier')}>
              <select className="input" value={supplier} onChange={(e) => setSupplier(e.target.value)} data-autofocus={!detail || undefined}>
                <option value="">{t('buy.pick_supplier')}</option>
                {suppliers
                  .filter((s) => s.active || s.id === supplier)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label={t('buy.expected')} hint={t('buy.expected_hint')}>
              <input className="input" type="date" value={expected} onChange={(e) => setExpected(e.target.value)} />
            </Field>
            {places.multi && !dropship ? (
              <Field label={t('stock.deliver_to')} hint={t('buy.deliver_to_hint')}>
                <select className="input" value={place} onChange={(e) => setPlace(e.target.value)}>
                  {places.places.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
          </div>
          <label className="switch-row">
            <span>
              <span className="switch-title">{t('ds.switch')}</span>
              <span className="muted">{t('ds.switch_hint')}</span>
            </span>
            <Switch checked={dropship} label={t('ds.switch')} onChange={setDropship} />
          </label>
          {dropship ? (
            <div className="form-grid ds-ship">
              <Field label={t('ds.ship_name')} required>
                <input className="input" value={ship.name} maxLength={120} onChange={(e) => setShip({ ...ship, name: e.target.value })} autoComplete="off" />
              </Field>
              <Field label={t('ds.customer_ref')} hint={t('ds.customer_ref_hint')}>
                <input className="input" value={ship.ref} maxLength={60} onChange={(e) => setShip({ ...ship, ref: e.target.value })} />
              </Field>
              <Field label={t('ds.ship_address')} required hint={t('ds.ship_address_needed')}>
                <input className="input" value={ship.address} maxLength={300} onChange={(e) => setShip({ ...ship, address: e.target.value })} />
              </Field>
              <Field label={t('ds.ship_phone')} hint={t('ds.ship_phone_hint')}>
                <input className="input" type="tel" value={ship.phone} maxLength={40} onChange={(e) => setShip({ ...ship, phone: e.target.value })} />
              </Field>
            </div>
          ) : null}
          <div className="stock-lines" role="table" aria-label={t('buy.order_lines')}>
            <div className="stock-line head" role="row">
              <span role="columnheader">{t('stock.item')}</span>
              <span role="columnheader">{t('stock.quantity')}</span>
              <span role="columnheader">{t('stock.cost_per_unit')}</span>
              <span role="columnheader" className="num">
                {t('stock.line_total')}
              </span>
              <span />
            </div>
            {lines.map((l, i) => {
              const p = parsed[i]!;
              const dupe = l.item && lines.filter((x) => x.item === l.item).length > 1;
              return (
                <div key={i} className="stock-line" role="row">
                  <select className={`input${dupe ? ' invalid' : ''}`} aria-label={t('stock.item')} value={l.item} onChange={(e) => set(i, 'item', e.target.value)}>
                    <option value="">{t('stock.pick_item')}</option>
                    {items.map((it) => (
                      <option key={it.id} value={it.id}>
                        {it.name}
                      </option>
                    ))}
                  </select>
                  <span className="input-unit">
                    <input
                      className={`input${l.qty && p.qty === null ? ' invalid' : ''}`}
                      inputMode="decimal"
                      aria-label={t('stock.quantity')}
                      value={l.qty}
                      onChange={(e) => set(i, 'qty', e.target.value)}
                      placeholder="0"
                    />
                    {p.item ? (
                      <PackSelect
                        packs={p.item.packs ?? []}
                        unitText={unitLabel(p.item.unit)}
                        value={l.pack}
                        label={t('packs.counted_in', { name: p.item.name })}
                        onChange={(v) => set(i, 'pack', v)}
                      />
                    ) : (
                      <span />
                    )}
                  </span>
                  <span className="input-unit">
                    <input
                      className={`input${l.cost && p.cost === null ? ' invalid' : ''}`}
                      inputMode="decimal"
                      aria-label={t('stock.cost_per_unit')}
                      value={l.cost}
                      onChange={(e) => set(i, 'cost', e.target.value)}
                      placeholder="0"
                    />
                    <span>{p.item ? `L / ${l.pack || unitLabel(p.item.unit)}` : 'L'}</span>
                    {l.listed ? (
                      <span className="sp-tag" title={t('sp.from_list')}>
                        <Icon name="bookmark" size={12} />
                      </span>
                    ) : null}
                  </span>
                  <span className="num">{p.qty && p.cost !== null ? money(Math.round(p.qty * p.cost)) : '—'}</span>
                  <IconButton icon="trash" label={t('stock.remove_line')} disabled={lines.length === 1} onClick={() => setLines(lines.filter((_, j) => j !== i))} />
                </div>
              );
            })}
          </div>
          {twice ? (
            <p className="form-error" role="alert">
              <Icon name="alert" size={14} /> {t('buy.twice')}
            </p>
          ) : null}
          <div className="row-actions">
            <Button icon="plus" onClick={() => setLines([...lines, { item: '', qty: '', cost: '', pack: '' }])}>
              {t('stock.add_line')}
            </Button>
            <Button icon="alert" onClick={suggest} disabled={!supplier || busy}>
              {t('buy.suggest')}
            </Button>
          </div>
          <Field label={t('buy.note')} hint={t('buy.note_hint')}>
            <input className="input" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
          </Field>
          {budget.data ? <BudgetLineSelect budget={budget.data} value={lineNow} onChange={setBudgetLine} amount={total} /> : null}
        </div>
      )}
    </Dialog>
  );
}

function OrderView({ detail: d, today, title, onClose, onChange }: { detail: OrderDetail; today: string; title: string; onClose: () => void; onChange: () => Promise<void> }) {
  const { can } = useSession();
  const manage = can('purchase.manage');
  const { busy, run } = useSave();
  const [receiving, setReceiving] = useState(false);
  const [landing, setLanding] = useState(false);
  const [landed, setLanded] = useState(0);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const o = d.order;
  const open = o.state === 'ordered' || o.state === 'partial';
  const { multi } = usePlaces();

  if (receiving) return <ReceiveDialog detail={d} onClose={() => setReceiving(false)} onChange={onChange} />;
  if (landing) return <LandedDialog order={o.id} onClose={() => setLanding(false)} onDone={async () => (setLanded((v) => v + 1), await onChange())} />;

  const cancel = () =>
    void run(
      async () => {
        await action('purchase.cancel_order', { order: o.id, reason: cancelling!.trim() });
        await onChange();
        onClose();
      },
      t('buy.cancelled', { name: o.name }),
    );
  const close = () =>
    void run(
      async () => {
        await action('purchase.close_order', { order: o.id });
        await onChange();
      },
      t('buy.closed', { name: o.name }),
    );

  return (
    <Dialog
      title={title}
      onClose={onClose}
      size="lg"
      footer={
        open ? (
          <>
            <span className="foot-actions">
              {manage && o.state === 'ordered' ? (
                <Button variant="ghost" icon="x" onClick={() => setCancelling('')}>
                  {t('buy.cancel_order')}
                </Button>
              ) : null}
              {manage && o.state === 'partial' ? (
                <Button variant="ghost" icon="check" onClick={close} busy={busy}>
                  {t('buy.close_order')}
                </Button>
              ) : null}
            </span>
            {can('purchase.receive') ? (
              <Button variant="primary" icon={o.dropship ? 'truck' : 'box'} onClick={() => setReceiving(true)}>
                {o.dropship ? t('ds.confirm') : t('buy.receive')}
              </Button>
            ) : null}
          </>
        ) : undefined
      }
    >
      <div className="form-stack">
        <div className="item-summary">
          <div>
            <span className="muted">{t('buy.supplier')}</span>
            <strong>{d.supplier.name}</strong>
          </div>
          {o.expected ? (
            <div>
              <span className="muted">{t('buy.expected')}</span>
              <strong>{day(o.expected)}</strong>
            </div>
          ) : null}
          {multi && o.location_name && !o.dropship ? (
            <div>
              <span className="muted">{t('stock.deliver_to')}</span>
              <strong>{o.location_name}</strong>
            </div>
          ) : null}
          <div>
            <span className="muted">{t('stock.line_total')}</span>
            <strong>{money(o.total)}</strong>
          </div>
          <OrderBadge o={o} today={today} />
        </div>
        {o.state !== 'cancelled' ? <BudgetLineBox document="purchase.order" record={o.id} editable={manage && open} onChange={onChange} /> : null}
        {o.dropship ? (
          <div className="ds-card" aria-label={t('ds.ship_to')}>
            <span className="list-icon move-landed" aria-hidden="true">
              <Icon name="truck" size={18} />
            </span>
            <span className="ds-card-main">
              <span className="muted small">{t('ds.ship_to')}</span>
              <strong>{o.ship_name}</strong>
              <span>{o.ship_address}</span>
              <span className="muted small">{[o.ship_phone, o.customer_ref ? t('ds.for_ref', { ref: o.customer_ref }) : null].filter(Boolean).join(' · ')}</span>
            </span>
          </div>
        ) : null}
        {o.note ? (
          <p className="callout">
            <Icon name="note" size={16} /> {o.note}
          </p>
        ) : null}
        {o.cancel_reason ? (
          <p className="callout callout-warn">
            <Icon name="x" size={16} /> {t('buy.cancel_reason', { reason: o.cancel_reason })}
          </p>
        ) : null}
        {d.supplier.phone && open ? (
          <p className="muted small">
            <Icon name="bell" size={14} /> {t('buy.call', { phone: d.supplier.phone })}
          </p>
        ) : null}
        {cancelling !== null ? (
          <div className="waste-form">
            <Field label={t('buy.cancel_why')}>
              <input className="input" value={cancelling} maxLength={200} onChange={(e) => setCancelling(e.target.value)} data-autofocus />
            </Field>
            <div className="form-actions">
              <Button variant="ghost" onClick={() => setCancelling(null)}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" icon="x" onClick={cancel} busy={busy} disabled={cancelling.trim().length < 3}>
                {t('buy.cancel_order')}
              </Button>
            </div>
          </div>
        ) : null}
        <div className="list-card">
          {d.lines.map((l) => {
            const left = Math.max(0, Math.round((l.qty - l.received) * 1000) / 1000);
            return (
              <div key={l.id} className="list-row">
                <span className="list-main">
                  <span className="list-title">{l.name}</span>
                  <span className="list-sub">
                    {l.pack && l.pack_qty
                      ? t('buy.line_sub', {
                          qty: t('packs.line', { n: Math.round((l.qty / l.pack_qty) * 1000) / 1000, pack: l.pack, units: withUnit(l.qty, l.unit) }),
                          cost: money(Math.round(l.unit_cost * l.pack_qty)),
                          unit: l.pack,
                        })
                      : t('buy.line_sub', { qty: withUnit(l.qty, l.unit), cost: money(Math.round(l.unit_cost)), unit: unitLabel(l.unit) })}
                  </span>
                </span>
                <span className="stock-qty">
                  <strong className={l.received >= l.qty ? 'ok' : l.received > 0 ? 'warn' : ''}>{t('buy.got', { got: withUnit(l.received, l.unit) })}</strong>
                  {left > 0 && o.state !== 'cancelled' && o.state !== 'received' ? <span className="muted">{t('buy.left', { qty: withUnit(left, l.unit) })}</span> : null}
                </span>
              </div>
            );
          })}
        </div>
        {d.receipts.length ? (
          <section className="item-moves">
            <h3>{t('buy.deliveries')}</h3>
            <div className="list-card">
              {d.receipts.map((r) => (
                <div key={r.id} className="list-row compact-move">
                  <span className="list-icon move-purchase" aria-hidden="true">
                    <Icon name="plus" size={18} />
                  </span>
                  <span className="list-main">
                    <span className="list-title">{r.name}</span>
                    <span className="list-sub">{[dateTime(r.at), r.by_name].filter(Boolean).join(' · ')}</span>
                  </span>
                  <span className="stock-qty">
                    <strong className="ok">+{withUnit(r.qty, r.unit)}</strong>
                    <span className="muted">{money(r.value)}</span>
                  </span>
                </div>
              ))}
            </div>
          </section>
        ) : null}
        {d.deliveries?.length ? (
          <section className="item-moves">
            <h3>{t('ds.deliveries')}</h3>
            <div className="list-card">
              {d.deliveries.map((r) => (
                <div key={r.id} className="list-row compact-move">
                  <span className="list-icon move-landed" aria-hidden="true">
                    <Icon name="truck" size={18} />
                  </span>
                  <span className="list-main">
                    <span className="list-title">{t('ds.delivered_to', { name: o.ship_name ?? '' })}</span>
                    <span className="list-sub">{[dateTime(r.at), r.by_name, r.note].filter(Boolean).join(' · ')}</span>
                  </span>
                  <strong>{money(r.value)}</strong>
                </div>
              ))}
            </div>
          </section>
        ) : null}
        {manage && (o.state === 'partial' || o.state === 'received') ? <LandedList order={o.id} version={landed} onAdd={() => setLanding(true)} /> : null}
        {d.bills.length ? (
          <section className="item-moves">
            <h3>{t('buy.tab_bills')}</h3>
            <div className="list-card">
              {d.bills.map((b) => (
                <div key={b.id} className="list-row compact-move">
                  <span className="list-icon" aria-hidden="true">
                    <Icon name="receipt" size={18} />
                  </span>
                  <span className="list-main">
                    <span className="list-title">{[billNo(b.number), b.supplier_ref].filter(Boolean).join(' · ')}</span>
                    <span className="list-sub">{t('buy.due_on', { day: day(b.due_date) })}</span>
                  </span>
                  <strong>{money(b.total)}</strong>
                  <BillBadge b={b} today={today} />
                </div>
              ))}
            </div>
          </section>
        ) : null}
        <RecordNotes model="purchase.order" record={o.id} version={d} />
      </div>
    </Dialog>
  );
}

/** What arrived: everything still due is filled in, change what differs. */
function ReceiveDialog({ detail: d, onClose, onChange }: { detail: OrderDetail; onClose: () => void; onChange: () => Promise<void> }) {
  const { can } = useSession();
  const manage = can('purchase.manage');
  const { busy, run } = useSave();
  const due = d.lines.filter((l) => l.received < l.qty);
  const r3 = (n: number) => Math.round(n * 1000) / 1000;
  // A line ordered in boxes is counted in boxes, unless what is left is not whole boxes.
  const [inPacks, setInPacks] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(due.map((l) => [l.id, !!(l.pack && l.pack_qty && Number.isInteger(r3((l.qty - l.received) / l.pack_qty)))])),
  );
  const perOf = (l: OrderLine, packs = inPacks[l.id]) => (packs && l.pack_qty ? l.pack_qty : 1);
  const [qty, setQty] = useState<Record<string, string>>(() => Object.fromEntries(due.map((l) => [l.id, String(r3((l.qty - l.received) / perOf(l)))])));
  const [cost, setCost] = useState<Record<string, string>>(() => Object.fromEntries(d.lines.map((l) => [l.id, costText(r3(l.unit_cost * perOf(l)))])));
  const switchPacks = (l: OrderLine, on: boolean) => {
    const from = perOf(l);
    const to = perOf(l, on);
    const q = parseQty(qty[l.id] ?? '');
    const c = parseCost(cost[l.id] ?? '');
    setInPacks({ ...inPacks, [l.id]: on });
    if (q !== null) setQty({ ...qty, [l.id]: String(r3((q * from) / to)) });
    if (c !== null) setCost({ ...cost, [l.id]: costText(r3((c / from) * to)) });
  };
  const [withBill, setWithBill] = useState(manage);
  const [ref, setRef] = useState('');
  const [date, setDate] = useState(todayIso());
  const [billTotal, setBillTotal] = useState('');
  const [tax, setTax] = useState('');
  const [note, setNote] = useState('');
  const [lots, setLots] = useState<Record<string, LotDraft>>({});
  const ds = !!d.order.dropship;
  // Lines already delivered in full are not asked again.
  const rows = due.map((l) => {
    const q = (qty[l.id] ?? '') === '' ? null : parseQty(qty[l.id]!);
    // Goods sent straight to the customer never enter stock, so they have no lots here.
    const units = q === null ? null : r3(q * perOf(l));
    return { l, q, units, c: parseCost(cost[l.id] ?? ''), lots: ds ? {} : readLots(l.tracking ?? 'none', units, lots[l.id] ?? emptyLots()) };
  });
  const taking = rows.filter((r) => r.q !== null && r.q > 0);
  const bad = rows.some((r) => (qty[r.l.id] ?? '') !== '' && r.q === null) || (manage && taking.some((r) => r.c === null)) || taking.some((r) => r.lots.problem);
  const value = taking.reduce((s, r) => s + Math.round(r.q! * (manage ? r.c! : r.l.unit_cost * perOf(r.l))), 0);
  const billTotalN = billTotal === '' ? value : parseMoney(billTotal);
  const taxN = tax === '' ? 0 : parseMoney(tax);
  const billBad = withBill && (billTotalN === null || taxN === null || !date);

  const save = () =>
    void run(
      async () => {
        await action('purchase.receive', {
          order: d.order.id,
          lines: taking.map((r) => ({
            line: r.l.id,
            qty: r.q,
            ...(inPacks[r.l.id] ? { in_packs: true } : {}),
            ...(manage && r.c !== r3(r.l.unit_cost * perOf(r.l)) ? { unit_cost: r.c } : {}),
            ...(r.lots.lots ? { lots: r.lots.lots } : {}),
          })),
          bill: withBill ? { supplier_ref: ref.trim() || null, date, total: billTotalN, tax: taxN } : undefined,
          note: note.trim() || null,
        });
        await onChange();
        onClose();
      },
      ds ? t('ds.confirmed_toast', { name: d.order.ship_name ?? '' }) : t('buy.received_toast', { value: money(value) }),
    );

  return (
    <Dialog
      title={ds ? t('ds.confirm_title', { name: d.order.name, customer: d.order.ship_name ?? '' }) : t('buy.receive_title', { name: d.order.name, supplier: d.supplier.name })}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="muted">{t('buy.receive_value', { value: money(value) })}</span>
          <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={!taking.length || bad || billBad}>
            {ds ? t('ds.confirm') : t('stock.receive_save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted small">{ds ? t('ds.confirm_hint') : t('buy.receive_hint')}</p>
        <div className="count-list">
          {rows.map(({ l, q, units }) => (
            <div key={l.id} className="count-row-wrap">
              <div className={`count-row${manage ? ' with-cost' : ''}`}>
                <span className="list-main">
                  <span className="list-title">{l.name}</span>
                  <span className="list-sub">{t('buy.ordered_got', { qty: withUnit(l.qty, l.unit), got: withUnit(l.received, l.unit) })}</span>
                </span>
                <span className="input-unit">
                  <input
                    className={`input${(qty[l.id] ?? '') !== '' && q === null ? ' invalid' : ''}`}
                    inputMode="decimal"
                    aria-label={t('buy.arrived_for', { name: l.name })}
                    value={qty[l.id] ?? ''}
                    placeholder="0"
                    onChange={(e) => setQty({ ...qty, [l.id]: e.target.value })}
                  />
                  {l.pack && l.pack_qty ? (
                    <PackSelect
                      packs={[{ name: l.pack, qty: l.pack_qty }]}
                      unitText={unitLabel(l.unit)}
                      value={inPacks[l.id] ? l.pack : ''}
                      label={t('packs.counted_in', { name: l.name })}
                      onChange={(v) => switchPacks(l, !!v)}
                    />
                  ) : (
                    <span>{unitLabel(l.unit)}</span>
                  )}
                </span>
                {manage ? (
                  <span className="input-unit">
                    <input
                      className="input"
                      inputMode="decimal"
                      aria-label={t('buy.cost_for', { name: l.name })}
                      value={cost[l.id] ?? ''}
                      onChange={(e) => setCost({ ...cost, [l.id]: e.target.value })}
                    />
                    <span>L / {inPacks[l.id] && l.pack ? l.pack : unitLabel(l.unit)}</span>
                  </span>
                ) : null}
              </div>
              {!ds && q ? (
                <LotsField name={l.name} unit={l.unit} tracking={l.tracking ?? 'none'} qty={units} value={lots[l.id] ?? emptyLots()} onChange={(v) => setLots({ ...lots, [l.id]: v })} />
              ) : null}
            </div>
          ))}
        </div>
        {ds ? (
          <Field label={t('ds.note')} hint={t('ds.note_hint')}>
            <input className="input" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
          </Field>
        ) : null}
        {manage ? (
          <>
            <label className="switch-row">
              <span>
                <span className="switch-title">{t('buy.with_bill')}</span>
                <span className="muted">{t('buy.with_bill_hint')}</span>
              </span>
              <Switch checked={withBill} label={t('buy.with_bill')} onChange={setWithBill} />
            </label>
            {withBill ? (
              <div className="form-grid four">
                <Field label={t('buy.supplier_ref')}>
                  <input className="input" value={ref} maxLength={60} onChange={(e) => setRef(e.target.value)} placeholder="e.g. 1184/2026" />
                </Field>
                <Field label={t('buy.bill_date')}>
                  <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </Field>
                <Field label={t('buy.bill_total')} hint={t('buy.bill_total_hint')} error={billTotalN === null ? t('buy.money_rule') : null}>
                  <input className="input" inputMode="decimal" value={billTotal} placeholder={String(value / 100)} onChange={(e) => setBillTotal(e.target.value)} />
                </Field>
                <Field label={t('buy.vat')} error={taxN === null ? t('buy.money_rule') : null}>
                  <input className="input" inputMode="decimal" value={tax} placeholder="0" onChange={(e) => setTax(e.target.value)} />
                </Field>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Bills */

function Bills({ today, onChange }: { today: string; onChange: () => Promise<void> }) {
  const [filter, setFilter] = useState<'open' | 'overdue' | 'paid' | 'all'>('open');
  const { data, reload } = useAction<Bill[]>('purchase.bills', { filter });
  // A link from elsewhere (an approval) opens one bill: /manage/purchasing?tab=bills&open=<id>.
  const [open, setOpen] = useState<string | null>(() => new URLSearchParams(window.location.search).get('open'));
  return (
    <>
      <div className="toolbar">
        <div className="segmented" role="radiogroup" aria-label={t('stock.show')}>
          {(['open', 'overdue', 'paid', 'all'] as const).map((f) => (
            <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
              {t(`buy.bf_${f}`)}
            </button>
          ))}
        </div>
      </div>
      <section className="list-card" aria-label={t('buy.tab_bills')}>
        {!data ? (
          <Skeleton h={160} />
        ) : data.length === 0 ? (
          <EmptyState icon="receipt" title={filter === 'overdue' ? t('buy.no_overdue') : t('buy.no_bills')} />
        ) : (
          data.map((b) => (
            <button key={b.id} className="list-row stock-row" onClick={() => setOpen(b.id)}>
              <span className="list-icon" aria-hidden="true">
                <Icon name="receipt" size={18} />
              </span>
              <span className="list-main">
                <span className="list-title">
                  {b.supplier_name} · {[billNo(b.number), b.supplier_ref].filter(Boolean).join(' / ')}
                </span>
                <span className="list-sub">
                  {[
                    day(b.date),
                    b.state === 'open' ? (b.days_overdue ? tn('buy.days_late', b.days_overdue) : t('buy.due_on', { day: day(b.due_date) })) : null,
                    b.order_number ? po(b.order_number) : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              <span className="stock-qty">
                {b.currency ? (
                  <>
                    <strong className={b.state === 'open' && b.due_date < today ? 'danger' : ''}>
                      {moneyIn(b.state === 'open' ? (b.fx_total ?? 0) - (b.fx_paid ?? 0) : (b.fx_total ?? 0), b.currency)}
                    </strong>
                    <span className="muted">{b.state === 'open' && b.paid ? t('buy.of_total', { total: moneyIn(b.fx_total ?? 0, b.currency) }) : money(b.total)}</span>
                  </>
                ) : (
                  <>
                    <strong className={b.state === 'open' && b.due_date < today ? 'danger' : ''}>{money(b.state === 'open' ? b.total - b.paid : b.total)}</strong>
                    {b.state === 'open' && b.paid ? <span className="muted">{t('buy.of_total', { total: money(b.total) })}</span> : null}
                  </>
                )}
              </span>
              <BillBadge b={b} today={today} />
            </button>
          ))
        )}
      </section>
      {open ? (
        <BillDialog
          id={open}
          today={today}
          onClose={() => setOpen(null)}
          onChange={async () => {
            await Promise.all([reload(), onChange()]);
          }}
        />
      ) : null}
    </>
  );
}

const PAY_METHODS: { key: PayMethod; icon: IconName }[] = [
  { key: 'bank', icon: 'send' },
  { key: 'cash', icon: 'cash' },
  { key: 'till', icon: 'register' },
  { key: 'card', icon: 'card' },
];

function BillDialog({ id, today, onClose, onChange }: { id: string; today: string; onClose: () => void; onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { can } = useSession();
  const { data: d, reload } = useAction<BillDetail>('purchase.bill', { bill: id });
  const { data: registers } = useAction<{ id: string; name: string; session: { id: string } | null }[]>('pos.registers');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PayMethod>('bank');
  const [register, setRegister] = useState('');
  const [reference, setReference] = useState('');
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [landing, setLanding] = useState(false);
  const [landed, setLanded] = useState(0);
  const openShifts = (registers ?? []).filter((r) => r.session);
  // A bill in another currency is paid in it, at the rate of the day it is paid.
  const cur = d?.bill.currency ?? null;
  const fm = (n: number) => moneyIn(n, cur);
  const [paidOn, setPaidOn] = useState(todayIso());
  const [rateTyped, setRateTyped] = useState('');
  const dayRate = useDayRate(cur ?? '', paidOn);
  const payRate = cur ? effectiveRate(rateTyped, dayRate) : null;
  const left = d ? (cur ? (d.bill.fx_total ?? 0) - (d.bill.fx_paid ?? 0) : d.bill.total - d.bill.paid) : 0;
  const amountN = amount === '' ? left : parseMoney(amount);
  const booked = d && cur && amountN ? (amountN === left ? d.bill.total - d.bill.paid : Math.min(toBase(amountN, d.bill.rate ?? 0), d.bill.total - d.bill.paid)) : 0;
  const outflow = cur && amountN && payRate ? toBase(amountN, payRate) : null;
  const methods = cur ? PAY_METHODS.filter((m) => m.key !== 'till') : PAY_METHODS;
  const tillSession = method === 'till' ? (openShifts.find((r) => r.id === register) ?? (openShifts.length === 1 ? openShifts[0] : undefined))?.session?.id : undefined;
  const refresh = async () => {
    await Promise.all([reload(), onChange()]);
  };
  const pay = () =>
    void run(
      async () => {
        await action('purchase.pay', {
          bill: id,
          amount: amountN,
          method,
          session: tillSession,
          reference: reference.trim() || null,
          ...(cur ? { paid_on: paidOn, rate: rateTyped.trim() ? Number(rateTyped.trim().replace(',', '.')) : null } : {}),
        });
        setAmount('');
        setReference('');
        await refresh();
      },
      t('buy.paid_toast', { amount: fm(amountN ?? 0), name: d?.supplier.name ?? '' }),
    );
  const cancel = () =>
    void run(
      async () => {
        await action('purchase.cancel_bill', { bill: id, reason: cancelling!.trim() });
        await refresh();
        onClose();
      },
      t('buy.bill_cancelled', { name: d?.bill.name ?? '' }),
    );

  if (landing && d) return <LandedDialog bill={d.bill.id} onClose={() => setLanding(false)} onDone={async () => (setLanded((v) => v + 1), await refresh())} />;

  return (
    <Dialog title={d ? `${d.bill.name} · ${d.supplier.name}` : t('buy.bill')} onClose={onClose} size="md">
      {!d ? (
        <Skeleton h={240} />
      ) : (
        <div className="form-stack">
          <div className="item-summary">
            <div>
              <span className="muted">{t('buy.bill_total')}</span>
              <strong>{cur ? fm(d.bill.fx_total ?? 0) : money(d.bill.total)}</strong>
              {cur ? <span className="fx-base">{t('fx.at_rate', { amount: money(d.bill.total), rate: rateText(d.bill.rate ?? 0) })}</span> : null}
            </div>
            <div>
              <span className="muted">{t('buy.vat')}</span>
              <strong>{cur ? fm(d.bill.fx_tax ?? 0) : money(d.bill.tax)}</strong>
            </div>
            <div>
              <span className="muted">{t('buy.left_to_pay')}</span>
              <strong className={d.bill.state === 'open' && d.bill.due_date < today ? 'text-danger' : ''}>{fm(left)}</strong>
            </div>
            <BillBadge b={d.bill} today={today} />
          </div>
          <dl className="report kv">
            {d.bill.supplier_ref ? (
              <>
                <dt>{t('buy.supplier_ref')}</dt>
                <dd>{d.bill.supplier_ref}</dd>
              </>
            ) : null}
            <dt>{t('buy.bill_date')}</dt>
            <dd>{day(d.bill.date)}</dd>
            <dt>{t('buy.due_date')}</dt>
            <dd>{day(d.bill.due_date)}</dd>
            {d.order ? (
              <>
                <dt>{t('buy.order')}</dt>
                <dd>{d.order.name}</dd>
              </>
            ) : null}
            {d.bill.note ? (
              <>
                <dt>{t('buy.note')}</dt>
                <dd>{d.bill.note}</dd>
              </>
            ) : null}
            {d.bill.cancel_reason ? (
              <>
                <dt>{t('buy.cancel_why')}</dt>
                <dd>{d.bill.cancel_reason}</dd>
              </>
            ) : null}
          </dl>
          {!d.order && d.bill.state !== 'cancelled' ? <BudgetLineBox document="purchase.bill" record={d.bill.id} editable={can('purchase.manage')} onChange={onChange} /> : null}

          {d.bill.state === 'open' ? (
            <section className="pay-box" aria-labelledby="pay-title">
              <h3 id="pay-title">{t('buy.pay')}</h3>
              <div className="segmented" role="radiogroup" aria-label={t('buy.pay_method')}>
                {methods.map((m) => (
                  <button key={m.key} role="radio" aria-checked={method === m.key} className={method === m.key ? 'active' : ''} onClick={() => setMethod(m.key)}>
                    <Icon name={m.icon} size={16} /> {t(`buy.pm_${m.key}`)}
                  </button>
                ))}
              </div>
              <div className="form-grid">
                <Field
                  label={cur ? t('fx.amount_in', { code: cur }) : t('shift.amount')}
                  hint={t('buy.amount_hint', { left: fm(left) })}
                  error={amountN === null ? t('buy.money_rule') : amountN > left ? t('buy.too_much', { left: fm(left) }) : null}
                >
                  <input className="input" inputMode="decimal" value={amount} placeholder={String(left / 100)} onChange={(e) => setAmount(e.target.value)} />
                </Field>
                {cur ? (
                  <>
                    <Field label={t('inv.paid_on')}>
                      <input className="input" type="date" value={paidOn} max={todayIso()} onChange={(e) => setPaidOn(e.target.value)} />
                    </Field>
                    <Field
                      label={t('fx.rate_label', { code: cur, base: baseCurrency() })}
                      hint={dayRate && !rateTyped.trim() ? t('fx.day_rate_hint', { date: day(dayRate.date) }) : t('fx.bank_rate_hint')}
                      error={!rateTyped.trim() && dayRate === null ? t('fx.no_rate_day', { code: cur, date: day(paidOn) }) : null}
                    >
                      <input
                        className="input mono"
                        inputMode="decimal"
                        value={rateTyped}
                        placeholder={dayRate ? String(dayRate.rate / 1_000_000) : ''}
                        onChange={(e) => setRateTyped(e.target.value)}
                      />
                    </Field>
                  </>
                ) : null}
                {method === 'till' ? (
                  <Field label={t('shift.register')} error={!openShifts.length ? t('buy.no_open_shift') : null}>
                    <select className="input" value={register || tillSession ? register || openShifts[0]?.id : ''} onChange={(e) => setRegister(e.target.value)} disabled={!openShifts.length}>
                      {openShifts.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                ) : (
                  <Field label={t('buy.reference')} hint={t('buy.reference_hint')}>
                    <input className="input" value={reference} maxLength={60} onChange={(e) => setReference(e.target.value)} />
                  </Field>
                )}
              </div>
              <div className="form-actions">
                {d.bill.paid === 0 && cancelling === null ? (
                  <Button variant="ghost" icon="x" onClick={() => setCancelling('')}>
                    {t('buy.cancel_bill')}
                  </Button>
                ) : null}
                <Button variant="primary" icon="check" onClick={pay} busy={busy} disabled={!amountN || amountN > left || (method === 'till' && !tillSession) || (!!cur && payRate === null)}>
                  {t('buy.pay_amount', { amount: fm(amountN ?? 0) })}
                </Button>
              </div>
              {cur && amountN && outflow !== null ? (
                <div className="fx-diff-preview" role="status">
                  <span>
                    {t('fx.pays_off')} <strong>{money(booked)}</strong>
                  </span>
                  <span>
                    {t('fx.paid_out')} <strong>{money(outflow)}</strong>
                  </span>
                  {booked !== outflow ? (
                    <span className={booked > outflow ? 'fx-gain' : 'fx-loss'}>
                      {t(booked > outflow ? 'fx.gain_label' : 'fx.loss_label')} <strong>{money(Math.abs(booked - outflow))}</strong>
                    </span>
                  ) : (
                    <span className="muted">{t('fx.no_difference')}</span>
                  )}
                </div>
              ) : null}
            </section>
          ) : null}
          {cancelling !== null ? (
            <div className="waste-form">
              <Field label={t('buy.cancel_why')}>
                <input className="input" value={cancelling} maxLength={200} onChange={(e) => setCancelling(e.target.value)} data-autofocus />
              </Field>
              <div className="form-actions">
                <Button variant="ghost" onClick={() => setCancelling(null)}>
                  {t('common.cancel')}
                </Button>
                <Button variant="danger" icon="x" onClick={cancel} busy={busy} disabled={cancelling.trim().length < 3}>
                  {t('buy.cancel_bill')}
                </Button>
              </div>
            </div>
          ) : null}

          {!d.order && d.bill.state !== 'cancelled' ? <LandedList bill={d.bill.id} billNet={d.bill.total - d.bill.tax} version={landed} onAdd={() => setLanding(true)} /> : null}
          <section className="item-moves">
            <h3>{t('buy.payments')}</h3>
            <div className="list-card">
              {d.payments.length ? (
                d.payments.map((p) => (
                  <div key={p.id} className="list-row compact-move">
                    <span className="list-icon move-purchase" aria-hidden="true">
                      <Icon name={PAY_METHODS.find((m) => m.key === p.method)!.icon} size={18} />
                    </span>
                    <span className="list-main">
                      <span className="list-title">{t(`buy.pm_${p.method}`)}</span>
                      <span className="list-sub">
                        {[day(p.paid_on), p.by_name, p.reference, p.fx_amount && p.rate ? t('fx.paid_at', { rate: rateText(p.rate), amount: money(p.amount - (p.fx_diff ?? 0)) }) : null]
                          .filter(Boolean)
                          .join(' · ')}
                        {p.fx_diff ? <span className={p.fx_diff > 0 ? 'fx-gain' : 'fx-loss'}> · {t(p.fx_diff > 0 ? 'fx.gain' : 'fx.loss', { amount: money(Math.abs(p.fx_diff)) })}</span> : null}
                      </span>
                    </span>
                    <strong>{p.fx_amount ? fm(p.fx_amount) : money(p.amount)}</strong>
                  </div>
                ))
              ) : (
                <p className="muted pad">{t('buy.no_payments')}</p>
              )}
            </div>
          </section>
        </div>
      )}
    </Dialog>
  );
}

/** A bill that did not come with an order: a delivery paid later, electricity, repairs. */
function NewBillDialog({ onClose, onChange }: { onClose: () => void; onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { data: suppliers } = useAction<Supplier[]>('purchase.suppliers');
  const [supplier, setSupplier] = useState('');
  const [ref, setRef] = useState('');
  const [date, setDate] = useState(todayIso());
  const [due, setDue] = useState('');
  const [total, setTotal] = useState('');
  const [tax, setTax] = useState('');
  const [note, setNote] = useState('');
  const { data: fxList } = useCurrencies();
  const [cur, setCur] = useState('');
  const [rateTyped, setRateTyped] = useState('');
  const dayRate = useDayRate(cur, date);
  const rate = cur ? effectiveRate(rateTyped, dayRate) : null;
  const budget = useBudgetFor('purchase.bill', null);
  const [budgetLine, setBudgetLine] = useState('');
  const sup = suppliers?.find((s) => s.id === supplier);
  const autoDue = useMemo(() => {
    if (!sup || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return '';
    const d = new Date(`${date}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + sup.payment_days);
    return d.toISOString().slice(0, 10);
  }, [sup, date]);
  const totalN = parseMoney(total);
  const taxN = tax === '' ? 0 : parseMoney(tax);
  const save = () =>
    void run(
      async () => {
        const bill = await action<{ id: string }>('purchase.create_bill', {
          supplier,
          supplier_ref: ref.trim() || null,
          date,
          due_date: due || null,
          total: totalN,
          tax: taxN,
          note: note.trim() || null,
          currency: cur || null,
          rate: cur && rateTyped.trim() ? Number(rateTyped.trim().replace(',', '.')) : null,
        });
        if (budget.data) await commitToLine('purchase.bill', bill.id, budgetLine);
        await onChange();
        onClose();
      },
      t('buy.bill_saved', { name: sup?.name ?? '' }),
    );
  return (
    <Dialog
      title={t('buy.new_bill')}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={!supplier || !totalN || taxN === null || (taxN ?? 0) > (totalN ?? 0) || (!!cur && rate === null)}>
            {t('buy.save_bill')}
          </Button>
        </>
      }
    >
      {!suppliers ? (
        <Skeleton h={240} />
      ) : (
        <div className="form-stack">
          <div className="form-grid">
            <Field label={t('buy.supplier')}>
              <select className="input" value={supplier} onChange={(e) => setSupplier(e.target.value)} data-autofocus>
                <option value="">{t('buy.pick_supplier')}</option>
                {suppliers
                  .filter((s) => s.active)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label={t('buy.supplier_ref')}>
              <input className="input" value={ref} maxLength={60} onChange={(e) => setRef(e.target.value)} />
            </Field>
            <Field label={t('buy.bill_date')}>
              <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field label={t('buy.due_date')} hint={sup ? tn('buy.due_hint', sup.payment_days) : undefined}>
              <input className="input" type="date" value={due || autoDue} onChange={(e) => setDue(e.target.value)} />
            </Field>
            {fxList && fxList.currencies.some((c) => c.active) ? (
              <CurrencyFields list={fxList} currency={cur} onCurrency={setCur} rate={rateTyped} onRate={setRateTyped} date={date} dayRate={dayRate} />
            ) : null}
            <Field
              label={cur ? t('fx.total_in_code', { code: cur }) : t('buy.bill_total')}
              hint={cur && totalN && rate ? t('fx.equals_base', { amount: money(toBase(totalN, rate)) }) : t('buy.with_vat')}
              error={total && totalN === null ? t('buy.money_rule') : null}
            >
              <input className="input" inputMode="decimal" value={total} onChange={(e) => setTotal(e.target.value)} />
            </Field>
            <Field label={t('buy.vat')} error={taxN === null ? t('buy.money_rule') : taxN > (totalN ?? 0) && totalN ? t('buy.vat_too_much') : null}>
              <input className="input" inputMode="decimal" value={tax} placeholder="0" onChange={(e) => setTax(e.target.value)} />
            </Field>
          </div>
          <Field label={t('buy.note')} hint={t('buy.bill_note_hint')}>
            <input className="input" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
          </Field>
          {budget.data ? <BudgetLineSelect budget={budget.data} value={budgetLine} onChange={setBudgetLine} amount={totalN ?? undefined} /> : null}
        </div>
      )}
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Suppliers */

function Suppliers({ manage, onChange }: { manage: boolean; onChange: () => Promise<void> }) {
  const { data, reload } = useAction<Supplier[]>('purchase.suppliers');
  const [open, setOpen] = useState<Supplier | null>(null);
  const [search, setSearch] = useState('');
  // The search finds a supplier: /manage/purchasing?tab=suppliers&q=<name>.
  useLinkParam('q', setSearch);
  const q = search.trim().toLowerCase();
  const list = (data ?? []).filter((s) => !q || s.name.toLowerCase().includes(q) || (s.tax_id ?? '').toLowerCase().includes(q));
  return (
    <>
      <div className="toolbar">
        <div className="search grow">
          <Icon name="search" size={18} />
          <input className="input" type="search" placeholder={t('buy.search_suppliers')} aria-label={t('buy.search_suppliers')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>
      <section className="list-card" aria-label={t('buy.tab_suppliers')}>
        {!data ? (
          <Skeleton h={160} />
        ) : list.length === 0 ? (
          <EmptyState icon="users" title={search ? t('order.no_results') : t('buy.no_suppliers')} />
        ) : (
          list.map((s) => (
            <button key={s.id} className={`list-row stock-row${s.active ? '' : ' archived'}`} onClick={() => setOpen(s)}>
              <span className="list-icon" aria-hidden="true">
                <Icon name="users" size={18} />
              </span>
              <span className="list-main">
                <span className="list-title">{s.name}</span>
                <span className="list-sub">
                  {[s.tax_id, s.phone, s.payment_days ? tn('buy.pays_in', s.payment_days) : t('buy.pays_now'), s.open_orders ? tn('buy.open_orders', s.open_orders) : null].filter(Boolean).join(' · ')}
                </span>
              </span>
              {manage ? (
                <span className="stock-qty">
                  <strong className={s.overdue ? 'danger' : ''}>{money(s.open_balance ?? 0)}</strong>
                  <span className="muted">{s.overdue ? t('buy.overdue_amount', { amount: money(s.overdue) }) : t('buy.owed')}</span>
                </span>
              ) : null}
              {!s.active ? <StatusBadge tone="neutral" icon="archive" label={t('stock.archived')} /> : s.overdue ? <StatusBadge tone="danger" icon="alert" label={t('buy.overdue')} /> : null}
            </button>
          ))
        )}
      </section>
      {open ? (
        <SupplierDialog
          supplier={open}
          readOnly={!manage}
          onClose={() => setOpen(null)}
          onChange={async () => {
            await Promise.all([reload(), onChange()]);
          }}
        />
      ) : null}
    </>
  );
}

function SupplierDialog({ supplier, readOnly, onClose, onChange }: { supplier?: Supplier; readOnly?: boolean; onClose: () => void; onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const cf = useCustomFields('purchase.supplier', supplier?.id);
  const [tried, setTried] = useState(false);
  const [v, setV] = useState({
    name: supplier?.name ?? '',
    tax_id: supplier?.tax_id ?? '',
    phone: supplier?.phone ?? '',
    email: supplier?.email ?? '',
    address: supplier?.address ?? '',
    payment_days: String(supplier?.payment_days ?? 0),
    note: supplier?.note ?? '',
    active: supplier?.active ?? true,
  });
  const [bank, setBank] = useState(bankDetailsOf(supplier));
  const us = useUsBanking();
  const bankBad = Object.keys(bankDetailsErrors(bank)).length > 0;
  const days = /^\d{1,3}$/.test(v.payment_days) ? Number(v.payment_days) : null;
  const tax = v.tax_id.trim().toUpperCase();
  // Checked by the business's own country pack: a supplier abroad has another format, which is fine.
  const ownTax = useTaxRates().rules?.taxId;
  const taxBad = tax !== '' && !!idError(ownTax, tax);
  const field = (k: keyof typeof v) => ({ value: v[k] as string, onChange: (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value }), disabled: readOnly });
  const save = () => {
    setTried(true);
    if (!cf.valid) return;
    void run(
      async () => {
        const values = {
          name: v.name.trim(),
          tax_id: tax || null,
          phone: v.phone.trim() || null,
          email: v.email.trim() || null,
          address: v.address.trim() || null,
          payment_days: days,
          ...bankDetailsValues(bank, us),
          note: v.note.trim() || null,
          active: v.active,
        };
        const saved = supplier ? await records.update<Supplier>('purchase.supplier', supplier.id, values) : await records.create<Supplier>('purchase.supplier', values);
        await cf.save(saved.id);
        await onChange();
        onClose();
      },
      supplier ? t('stock.saved', { name: v.name.trim() }) : t('stock.added', { name: v.name.trim() }),
    );
  };
  return (
    <Dialog
      title={supplier ? supplier.name : t('buy.new_supplier')}
      onClose={onClose}
      size="md"
      footer={
        readOnly ? undefined : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={!v.name.trim() || days === null || bankBad}>
              {supplier ? t('common.save') : t('buy.add_supplier')}
            </Button>
          </>
        )
      }
    >
      <div className="form-stack">
        <div className="form-grid">
          <Field label={t('stock.name')}>
            <input className="input" maxLength={120} {...field('name')} data-autofocus={!supplier || undefined} />
          </Field>
          <Field label={t('buy.tax_id')} hint={taxBad ? t('buy.tax_id_foreign') : idHint(ownTax)}>
            <input className="input" maxLength={30} {...field('tax_id')} />
          </Field>
          <Field label={t('buy.phone')}>
            <input className="input" type="tel" maxLength={40} {...field('phone')} />
          </Field>
          <Field label={t('buy.email')}>
            <input className="input" type="email" maxLength={120} {...field('email')} />
          </Field>
          <Field label={t('buy.address')}>
            <input className="input" maxLength={200} {...field('address')} />
          </Field>
          <Field label={t('buy.payment_days')} hint={t('buy.payment_days_hint')} error={days === null ? t('buy.days_rule') : null}>
            <input className="input" inputMode="numeric" maxLength={3} {...field('payment_days')} />
          </Field>
        </div>
        <BankDetailsFields value={bank} onChange={setBank} disabled={readOnly} />
        <Field label={t('buy.note')}>
          <input className="input" maxLength={500} {...field('note')} />
        </Field>
        {supplier && !readOnly ? (
          <label className="switch-row">
            <span>
              <span className="switch-title">{t('stock.in_use')}</span>
              <span className="muted">{t('buy.in_use_hint')}</span>
            </span>
            <Switch checked={v.active} label={t('stock.in_use')} onChange={(active) => setV({ ...v, active })} />
          </label>
        ) : null}
        {supplier ? <SupplierPrices supplier={supplier.id} manage={!readOnly} /> : null}
        {readOnly ? supplier ? <CustomFieldsView target="purchase.supplier" record={supplier.id} /> : null : <CustomFieldInputs cf={cf} tried={tried} />}
      </div>
    </Dialog>
  );
}

/** How late what we owe suppliers is, by age (purchase.aged). */
function AgedPayables() {
  const { data } = useAction<AgedData>('purchase.aged');
  return (
    <section className="panel" aria-labelledby="po-aged">
      <div className="panel-head">
        <div>
          <h2 id="po-aged">{t('fu.payables_title')}</h2>
          <p className="muted">{t('fu.payables_hint')}</p>
        </div>
      </div>
      {!data ? <Skeleton h={120} /> : <AgedTable data={data} who={t('buy.supplier')} label={t('fu.payables_title')} />}
    </section>
  );
}
